import React from 'react';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import BlockIcon from '@mui/icons-material/Block';
import { Link } from 'react-router-dom';
import { UserKindEnum } from '../../../../types/enums';
import { User } from '../types/types';

const USER_COLUMN_WIDTH = 372;

interface UserTableProps {
  users: User[];
  total: number;
  handleMenuClick: (event: React.MouseEvent<Element>, userId: number) => void;
}

const StyledTableCell = styled(TableCell)({
  border: 0,
  borderRight: '1px solid #E9EEF1',
  '&:last-child': {
    borderRight: 'none',
  },
  verticalAlign: 'middle',
  '&.header-cell': {
    fontWeight: 600,
    fontSize: '15px',
  },
  '&.all-cell': {
    minWidth: '270px',
  },
  '&.registration-cell': {
    maxWidth: '150px',
  },
  '&.social-cell': {
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  '&.frozen-cell': {
    position: 'sticky',
    zIndex: 1,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  '&.header-cell.frozen-cell': {
    zIndex: 2,
  },
  '&.user-column': {
    left: 0,
    width: `${USER_COLUMN_WIDTH}px`,
    minWidth: `${USER_COLUMN_WIDTH}px`,
    maxWidth: `${USER_COLUMN_WIDTH}px`,
    boxShadow: '2px 0 4px -3px rgba(42, 56, 66, 0.35)',
  },
});

const StyledIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 2px solid #c5dfff;
  border-radius: 14px;
`;

const UserCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

const UserIdentity = styled('div')`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 10px;

  & svg {
    margin-left: 5px;
    vertical-align: text-top;
  }
`;

const Avatar = styled('img')`
  margin-right: 10px;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

const UserInfo = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

const UserNameRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const UserNameLink = styled(Link)`
  font-weight: bold;
  font-size: 18px;
  color: #1650ff;
  text-decoration: none;
  cursor: pointer;
  min-width: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #1650ff;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const UserRole = styled('div')`
  color: rgba(42, 56, 66, 1);
  font-size: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const UserAgency = styled('span')`
  color: #01439b;
`;

const UserDate = styled('span')`
  font-size: 15px;
`;

const UserLink = styled('a')`
  color: #1650ff;
  &:hover {
    text-decoration: none;
  }
  &:visited,
  &:focus {
    color: #1650ff;
  }
`;

const StyledTableContainer = styled(TableContainer)({
  background: 'transparent',
  boxShadow: 'none',
  maxWidth: '1003px',
  overflowX: 'auto',
});

const StyledTableRow = styled(TableRow)({
  border: '1px solid #E9EEF1',
  background: 'inherit',
});

const StyledTableHead = styled(TableHead)({
  background: '#fff',
});

const formatDate = (value: string | null) =>
  value !== null ? moment(value).format('D MMMM YYYY, h:mm a') : null;

export const VerificationRequestsTable: React.FC<UserTableProps> = ({
  users,
  total,
  handleMenuClick,
}) => {
  return (
    <StyledTableContainer>
      <Table>
        <StyledTableHead>
          <StyledTableRow>
            <StyledTableCell className='header-cell all-cell frozen-cell user-column'>
              All requests({total})
            </StyledTableCell>
            <StyledTableCell className='header-cell'>Ambassador</StyledTableCell>
            <StyledTableCell className={`header-cell registration-cell`}>
              Date of registration
            </StyledTableCell>
            <StyledTableCell className={`header-cell`}>Email or Phone num.</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Social networks</StyledTableCell>
          </StyledTableRow>
        </StyledTableHead>
        <TableBody>
          {users.map((user, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell className='frozen-cell user-column'>
                <UserCell>
                  <UserIdentity>
                    {user.avatar && <Avatar src={user.avatar} />}
                    <UserInfo>
                      <UserNameRow>
                        <UserNameLink to={`/users/${user.id}`}>{user.name}</UserNameLink>
                        {user.status === 'blocked' && <BlockIcon fontSize='small' />}
                      </UserNameRow>
                      <UserRole>
                        {user.kind}
                        {user.employer.name && (
                          <>
                            {' '}
                            | <UserAgency>{user.employer.name}</UserAgency>
                          </>
                        )}
                      </UserRole>
                    </UserInfo>
                  </UserIdentity>
                  {user.kind !== UserKindEnum.Admin && (
                    <StyledIconButton
                      aria-label='more'
                      aria-controls='user-menu'
                      aria-haspopup='true'
                      onClick={(event) => handleMenuClick(event, user.id)}
                    >
                      <MoreVertIcon />
                    </StyledIconButton>
                  )}
                </UserCell>
              </StyledTableCell>
              <StyledTableCell>
                {user.isAmbassador && <Chip color='primary' label='Yes' size='small' />}
              </StyledTableCell>
              <StyledTableCell className={`registration-cell`}>
                <UserDate>{formatDate(user.createdAt)}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate>{user.email || user.phone}</UserDate>
              </StyledTableCell>
              <StyledTableCell className={`social-cell`}>
                <UserDate>
                  {user.links.map((link, index) => (
                    <React.Fragment key={index}>
                      <UserLink target='_blank' href={link}>
                        {link}
                      </UserLink>
                      {index < user.links.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </UserDate>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
